import type { DropResult } from '@hello-pangea/dnd'
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd'
import { TestAnswerFeedbackDisplayOption } from '@vedalib/editor/lib/elements'
import * as R from 'ramda'

import TestFeedback from 'components/Editor/EditorElements/courseKit/TestFeedback'
import {
  getFeedbackValue,
  onChangeRTGeneralFb,
} from 'components/Editor/EditorElements/utils/getFeedbackValue'
import { useGetRichTextProps } from 'components/form/RichText/useGetRichTextProps'

import type { TestMultipleChoiceElementType } from '../testMultipleChoiceTypes'
import * as s from './TestMultipleChoiceElement.module.scss'
import TestMultipleChoiceElementEditorItem from './TestMultipleChoiceElementEditorItem'

const TestMultipleChoiceElementEditor: TestMultipleChoiceElementType = ({
  element,
  mode,
  styles,
  waiting,
  onChange,
  font,
  feedback,
  state,
}) => {
  const { id: elementId, value } = element
  const { items } = value
  const { generalFbValid } = state || {}

  const { isActiveElement, activeIndex, onLabelSelect, isFill } = useGetRichTextProps({
    elementId,
    mode,
  })
  const feedbackValue = getFeedbackValue(value)

  const handleMove = ({ destination, source }: DropResult) =>
    destination &&
    onChange?.(R.assoc('items', R.move(source.index, destination.index, items), value))

  return (
    <>
      <DragDropContext onDragEnd={handleMove}>
        <Droppable
          droppableId={elementId}
          renderClone={(provided, { isDragging }, rubric) => (
            <TestMultipleChoiceElementEditorItem
              element={element}
              feedback={feedback}
              font={font}
              index={rubric.source.index}
              mode={mode}
              onChange={onChange}
              provided={provided}
              showControls={!isDragging}
              styles={styles}
              waiting={waiting}
            />
          )}
        >
          {(provided, { isDraggingOver }) => (
            <div
              className={s.items}
              ref={provided.innerRef}
              style={{ gap: styles.offset.marginTop }}
              {...provided.droppableProps}
            >
              {items.map((item, index) => (
                <Draggable draggableId={item.value} index={index} key={item.value}>
                  {(provided) => (
                    <TestMultipleChoiceElementEditorItem
                      element={element}
                      feedback={feedback}
                      font={font}
                      index={index}
                      mode={mode}
                      onChange={onChange}
                      provided={provided}
                      showControls={!isDraggingOver}
                      styles={styles}
                      waiting={waiting}
                    />
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <TestFeedback
        feedbackValue={feedbackValue}
        font={font}
        isTrueAnswer={generalFbValid}
        mode={mode}
        onChange={onChangeRTGeneralFb(value, onChange)}
        rtProps={{
          activeIndex,
          isActiveElement,
          onLabelSelect,
          isFill,
          value: value.generalFeedback,
        }}
        showFeedback={
          value.displayFeedback && value.displayOption !== TestAnswerFeedbackDisplayOption.perOption
        }
        styles={feedback}
      />
    </>
  )
}

export default TestMultipleChoiceElementEditor
